import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

/**
 * Todo lo que un comercio puede hacer sobre lo suyo desde el portal: su ficha
 * de negocio, sus eventos de Qué plan hoy y sus vacantes.
 *
 * Los criterios de propiedad y los formatos de campo replican los de la app
 * (`recnet-24`), no se inventan: si el portal guardara una fecha o unas
 * coordenadas con otra forma, el registro dejaría de verse en el celular.
 */

const normalizar = (correo) => String(correo || "").toLowerCase().trim();

// ===
// Ficha del negocio
// ===

/**
 * Busca el negocio de un correo.
 *
 * La app consulta por `register_by` y, si no encuentra nada, por `user_email`
 * en minúsculas (ver `DatosNegocio/index.js`): hay fichas viejas guardadas con
 * un criterio y nuevas con el otro. Buscar por uno solo deja a parte de los
 * comercios sin su negocio.
 */
export async function buscarNegocioDe(correo) {
  const db = firebase.firestore();
  const original = String(correo || "").trim();
  const minusculas = normalizar(correo);

  const intentos = [
    ["register_by", original],
    ["register_by", minusculas],
    ["user_email", minusculas],
  ];

  for (const [campo, valor] of intentos) {
    if (!valor) continue;
    const snap = await db.collection("negocios").where(campo, "==", valor).limit(1).get();
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
  }
  return null;
}

/**
 * Guarda la ficha. Si `docId` viene vacío se crea una nueva: un comercio puede
 * haber completado su alta en la app sin llegar a registrar su negocio.
 */
export async function guardarNegocio(datos, docId) {
  const db = firebase.firestore();
  const correo = firebase.auth().currentUser.email;

  const contenido = {
    ...datos,
    register_by: correo,
    user_email: normalizar(correo),
    // Mismo formato que escribe la app (`UserForm/index.js`).
    register_date: datos.register_date || moment().format("YYYY-MM-DD HH:mm:ss"),
  };

  if (docId) {
    await db.collection("negocios").doc(docId).update(contenido);
    return docId;
  }
  const ref = await db.collection("negocios").add(contenido);
  return ref.id;
}

// ===
// Eventos de Qué plan hoy
// ===

/** Eventos creados por este correo. */
export async function misEventos(correo) {
  const snap = await firebase
    .firestore()
    .collection("queplanhoy")
    .where("correoUsuario", "==", correo)
    .get();

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => milisegundos(b.creadoEn) - milisegundos(a.creadoEn));
}

export async function guardarEvento(datos, docId) {
  const db = firebase.firestore();
  const usuario = firebase.auth().currentUser;

  if (docId) {
    await db.collection("queplanhoy").doc(docId).update(datos);
    return docId;
  }

  const ref = await db.collection("queplanhoy").add({
    ...datos,
    correoUsuario: usuario.email,
    usuarioId: usuario.uid,
    creadoEn: new Date(),
  });
  return ref.id;
}

export function eliminarEvento(docId) {
  return firebase.firestore().collection("queplanhoy").doc(docId).delete();
}

// ===
// Vacantes y candidatos
// ===

export async function misVacantes(correo) {
  const snap = await firebase
    .firestore()
    .collection("vacantes")
    .where("register_by", "==", correo)
    .get();

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.fechaPublicacion || "").localeCompare(String(a.fechaPublicacion || "")));
}

export async function guardarVacante(datos, docId) {
  const db = firebase.firestore();
  const usuario = firebase.auth().currentUser;

  if (docId) {
    await db.collection("vacantes").doc(docId).update(datos);
    return docId;
  }

  const ref = await db.collection("vacantes").add({
    ...datos,
    register_by: usuario.email,
    uidPublicador: usuario.uid,
    fechaPublicacion: new Date().toISOString(),
    active: true,
  });
  return ref.id;
}

export function eliminarVacante(docId) {
  return firebase.firestore().collection("vacantes").doc(docId).delete();
}

/**
 * Candidatos de una vacante.
 *
 * `candidatos` es una colección de primer nivel, no una subcolección, y se
 * relaciona con la vacante por el **título del puesto** más el correo de quien
 * la publicó (ver `Candidatos/index.js` en la app). Por eso cambiar el título
 * de una vacante la desliga de sus postulaciones.
 */
export async function candidatosDe(vacante) {
  if (!vacante || !vacante.tituloPuesto || !vacante.register_by) return [];

  const snap = await firebase
    .firestore()
    .collection("candidatos")
    .where("puesto", "==", vacante.tituloPuesto)
    .where("register_by", "==", vacante.register_by)
    .get();

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ===
// Imágenes
// ===

/**
 * Sube un archivo a Storage y devuelve su URL pública.
 * Mismas carpetas que usa la app, para que no queden imágenes dispersas.
 */
export async function subirImagen(archivo, carpeta) {
  const extension = (archivo.name || "").split(".").pop() || "png";
  const ref = firebase.storage().ref(`${carpeta}/${uuidv4()}.${extension}`);
  const resultado = await ref.put(archivo);
  return resultado.ref.getDownloadURL();
}

// ===
// Helpers privados
// ===

function milisegundos(valor) {
  if (!valor) return 0;
  if (typeof valor.toMillis === "function") return valor.toMillis();
  if (typeof valor.toDate === "function") return valor.toDate().getTime();
  const fecha = new Date(valor);
  return isNaN(fecha.getTime()) ? 0 : fecha.getTime();
}
